import { useEffect, useState } from "react";
import Shell from "../components/Shell";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Plus, Trash2, X, DollarSign } from "lucide-react";

const BLANK = { amount: 0, carrier: "", product: "", policy_number: "", client_name: "", status: "pending", notes: "" };

export default function Commissions() {
  const [items, setItems] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(BLANK);

  const load = async () => { const { data } = await api.get("/commissions"); setItems(data); };
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    try {
      await api.post("/commissions", { ...form, amount: Number(form.amount) || 0 });
      toast.success("Commission logged");
      setShowModal(false); setForm(BLANK); load();
    } catch { toast.error("Failed"); }
  };

  const togglePaid = async (c) => {
    try {
      const payload = { ...c, status: c.status === "paid" ? "pending" : "paid" };
      delete payload.id; delete payload.user_id; delete payload.created_at;
      await api.patch(`/commissions/${c.id}`, payload);
      load();
    } catch { toast.error("Failed"); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this commission?")) return;
    try { await api.delete(`/commissions/${id}`); load(); } catch { toast.error("Failed"); }
  };

  const totalPaid = items.filter(i => i.status === "paid").reduce((s, i) => s + (i.amount || 0), 0);
  const totalPending = items.filter(i => i.status === "pending").reduce((s, i) => s + (i.amount || 0), 0);

  return (
    <Shell>
      <div className="p-4 sm:p-8" data-testid="commissions-page">
        <div className="flex items-end justify-between flex-wrap gap-3 mb-6">
          <div>
            <div className="label-xs text-amber">&gt; PAYOUT_LEDGER</div>
            <h1 className="font-display text-3xl sm:text-5xl leading-none mt-2">Commissions.</h1>
          </div>
          <button onClick={() => setShowModal(true)} className="btn-primary" data-testid="add-commission-btn"><Plus size={14} /> Log</button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-term border border-term mb-6">
          <div className="bg-raised p-5">
            <div className="label-xs">PAID</div>
            <div className="font-display text-4xl mt-2 text-gain">${totalPaid.toLocaleString()}</div>
          </div>
          <div className="bg-raised p-5">
            <div className="label-xs">PENDING</div>
            <div className="font-display text-4xl mt-2 text-warn">${totalPending.toLocaleString()}</div>
          </div>
          <div className="bg-raised p-5">
            <div className="label-xs">TOTAL LOGGED</div>
            <div className="font-display text-4xl mt-2 text-amber">${(totalPaid + totalPending).toLocaleString()}</div>
          </div>
        </div>

        <div className="border border-term bg-raised overflow-x-auto">
          <div className="min-w-[720px]">
          <div className="grid grid-cols-[2fr,1fr,1fr,1fr,1fr,80px] gap-2 px-4 py-3 border-b border-term label-xs">
            <div>CLIENT / CARRIER</div><div>PRODUCT</div><div>POLICY #</div><div>AMOUNT</div><div>STATUS</div><div></div>
          </div>
          {items.length === 0 && <div className="p-6 text-xs text-fg-muted font-mono">No commissions logged yet.</div>}
          {items.map((c) => (
            <div key={c.id} className="tap-row grid grid-cols-[2fr,1fr,1fr,1fr,1fr,80px] gap-2 px-4 py-3 text-xs font-mono items-center">
              <div>
                <div className="text-fg font-bold">{c.client_name || "—"}</div>
                <div className="text-fg-subtle text-[10px]">{c.carrier}</div>
              </div>
              <div className="text-fg-muted">{c.product || "—"}</div>
              <div className="text-fg-muted">{c.policy_number || "—"}</div>
              <div className="text-amber font-bold">${c.amount.toLocaleString()}</div>
              <div>
                <button onClick={() => togglePaid(c)} className={`text-[10px] uppercase tracking-widest px-2 py-1 border ${c.status === "paid" ? "border-gain text-gain" : "border-warn text-warn"}`}>
                  {c.status}
                </button>
              </div>
              <button onClick={() => del(c.id)} className="text-fg-subtle hover:text-live justify-self-end"><Trash2 size={12} /></button>
            </div>
          ))}
          </div>
        </div>

        {showModal && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" onClick={() => setShowModal(false)}>
            <form onClick={(e) => e.stopPropagation()} onSubmit={save} className="bg-raised border border-term p-6 max-w-lg w-full">
              <div className="flex justify-between items-center mb-5">
                <div>
                  <div className="label-xs text-amber">&gt; NEW</div>
                  <div className="font-display text-2xl mt-1">Log Commission</div>
                </div>
                <button type="button" onClick={() => setShowModal(false)} className="text-fg-muted"><X size={18} /></button>
              </div>
              <div className="space-y-3">
                <input required placeholder="Client Name" className="input-term" value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })} />
                <div className="grid grid-cols-2 gap-3">
                  <input placeholder="Carrier" className="input-term" value={form.carrier} onChange={(e) => setForm({ ...form, carrier: e.target.value })} />
                  <input placeholder="Product (Term, IUL, etc)" className="input-term" value={form.product} onChange={(e) => setForm({ ...form, product: e.target.value })} />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <input placeholder="Policy #" className="input-term" value={form.policy_number} onChange={(e) => setForm({ ...form, policy_number: e.target.value })} />
                  <input required type="number" placeholder="Amount $" className="input-term" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} />
                </div>
                <select className="input-term" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                  <option value="pending">Pending</option>
                  <option value="paid">Paid</option>
                </select>
                <textarea placeholder="Notes" rows={2} className="input-term" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
              </div>
              <button type="submit" className="btn-primary mt-5 w-full">Save &gt;</button>
            </form>
          </div>
        )}
      </div>
    </Shell>
  );
}
